import React, { useEffect, useMemo, useRef } from 'react'
import { useQuery } from '@tanstack/react-query'
import { MenuItem } from '@mui/material'
import { Role } from '@data/users/role'
import type { IRole } from '@data/users/role'
import { useRouteQuery, useTranslator } from '@/hooks'
import IconButton from '@mui/material/IconButton'
import Box from '@mui/material/Box'
import { InputField } from '../common'

interface RoleSelectionProps {
  value?: string | string[] | number | number[]
  multiple?: boolean
  onChange?: (value: string, selectedRole?: IRole) => void
  onClear?: () => void
  disabled?: boolean
  required?: boolean
  label?: string
  placeholder?: string
  className?: string
  textAlign?: React.CSSProperties['textAlign']
  height?: string | number
  error?: boolean
  helperText?: string
  adminOnly?: boolean
  agentPlayerOnly?: boolean
  companyId?: number
}

const RoleSelection = ({
  value = '',
  multiple = false,
  onChange,
  onClear,
  disabled = false,
  label = '',
  placeholder = '',
  height = '38px',
  error = false,
  helperText = '',
  adminOnly = false,
  agentPlayerOnly = false,
  companyId
}: RoleSelectionProps) => {
  const { serializeQuery } = useRouteQuery()
  const { __ } = useTranslator()
  const searchInputRef = useRef<HTMLInputElement>(null)

  const { data } = useQuery({
    ...Role.objects.paginateQueryFactory()
  })

  const { data: selectedRoles } = useQuery({
    ...Role.objects.paginateQueryFactory(
      serializeQuery({
        ids: Array.isArray(value) ? value : [value]
      })
    ),
    enabled: !!value && (Array.isArray(value) ? value.length > 0 : true)
  })

  useEffect(() => {
    if (data) {
      setTimeout(() => searchInputRef.current?.focus())
    }
  }, [data])

  const options = useMemo(() => {
    const paginateOptions = data ? Role.getRolesSelectOptions(data.data) : []
    const selectedOptions = selectedRoles ? Role.getRolesSelectOptions(selectedRoles.data) : []
    const combinedOptions = [...paginateOptions]

    selectedOptions.forEach(option => {
      if (!combinedOptions.some(opt => opt.value === option.value)) {
        combinedOptions.push(option)
      }
    })

    if (agentPlayerOnly) {
      return combinedOptions.filter(option => [7, 8].includes(option.value))
    }

    if (adminOnly) {
      return combinedOptions.filter(option => ![7, 8].includes(option.value))
    }

    let filtered = combinedOptions

    if (companyId === 1) {
      filtered = filtered.filter(option => option.value !== 4)
    }

    return filtered
  }, [data, selectedRoles, adminOnly, agentPlayerOnly, companyId])

  return (
    <InputField
      select
      value={value}
      label={label}
      onChange={e =>
        onChange?.(
          e.target.value,
          data?.data.find(b => b?.id === Number(e.target.value))
        )
      }
      placeholder={placeholder}
      fullWidth
      error={error}
      helperText={helperText}
      disabled={disabled}
      SelectProps={{
        multiple,
        displayEmpty: true,
        renderValue: (selected: any) => {
          if (!selected || (Array.isArray(selected) && selected.length === 0)) {
            return <span style={{ color: '#aaa' }}>{placeholder || label || __('Select...')}</span>
          }

          if (multiple && Array.isArray(selected)) {
            const selectedLabels = options
              .filter(option => selected.map(String).includes(String(option.value)))
              .map(option => option.label)
              .join(', ')

            return <>{selectedLabels}</>
          }

          const found = options.find(option => String(option.value) === String(selected))

          return <>{found ? found.label : selected}</>
        }
      }}
      slotProps={{
        input: {
          endAdornment:
            onClear && value && ((multiple && Array.isArray(value) && value.length > 0) || (!multiple && value)) ? (
              <Box mr={5}>
                <IconButton
                  size='small'
                  onClick={() => {
                    onClear?.()
                  }}
                  aria-label='clear selection'
                  tabIndex={-1}
                >
                  <i className='tabler-x text-[16px] text-textPrimary'></i>
                </IconButton>
              </Box>
            ) : null
        }
      }}
      sx={{
        '& .MuiSelect-select': {
          textAlign: 'left'
        },
        '& .MuiInputBase-root': {
          height: height
        }
      }}
    >
      {options.map(option => (
        <MenuItem onKeyDown={e => e.stopPropagation()} key={option.value} value={option.value}>
          {option.label}
        </MenuItem>
      ))}
    </InputField>
  )
}

export default RoleSelection
